/* @flow */
import * as React from 'react';
import { isEmpty } from 'lodash';
import { graphql } from 'react-apollo';
import pluralize from 'pluralize';

import Columns2 from 'components/Columns2';
import openUrl from 'utils/openUrl';
import LinkConstants from 'constants/LinkConstants';
import ConfirmDialog from 'components/ConfirmDialog';
import ModalTrigger from 'components/ModalTrigger';
import Box from 'components/Box';
import BoxHeader from 'components/BoxHeader';
import BoxBody from 'components/BoxBody';
import Buttons from 'components/Buttons';
import Button from 'components/Button';
import SEND_ACTIVATION_NOTIFICATIONS_MUTATION from 'admin/queries/SendActivationNotificationsMutation.gql';
import ToastActions from 'actions/ToastActions';

import type {
  sendActivationNotifications,
  SendActivationNotificationsInput,
} from 'admin/queries/__generated__/sendActivationNotifications';

type Props = {
  orgUuid: string,
  canNotifyTeachers: boolean,
  canNotifyStudents: boolean,
  canNotifyParents: boolean,
  sendActivationNotifications: SendActivationNotificationsInput => Promise<{
    data: sendActivationNotifications,
  }>,
};

type State = {
  notifiedGroups: Array<string>,
};

function rolesDescription(roles: string[]) {
  return roles.map(r => pluralize(r)).join(' and ');
}

class AdminSisSyncNotificationOptions extends React.Component<Props, State> {
  state: State = {
    notifiedGroups: [],
  };

  handleConfirmSendNotificationsToRoles = (roles: string[]) => {
    const { notifiedGroups } = this.state;

    this.setState({
      notifiedGroups: [...notifiedGroups, ...roles],
    });

    this.sendNotificationsToRoles(roles);
  };

  previewMessage(roles: string[]) {
    if (roles.includes('teacher')) {
      openUrl(LinkConstants.CLEVER_TEACHER_NOTIFICATION);
    } else if (roles.includes('student') || roles.includes('parent')) {
      openUrl(LinkConstants.CLEVER_STUDENT_NOTIFICATION);
    }
  }

  sendNotificationsToRoles(roles: string[]) {
    const { orgUuid } = this.props;
    this.props
      .sendActivationNotifications({
        orgUuid,
        roles,
      })
      .then(({ data }) => {
        const { error } = data.sendActivationNotifications;
        if (error) {
          ToastActions.showErrorToast(error.message);
        }
      })
      .catch(e => {
        console.error('could not send activation notifications', e);
      });
  }

  renderSendNotificationsModal(roles: string[], notificationType: string) {
    const rolesString = rolesDescription(roles);
    return (
      <ConfirmDialog
        title={`Notify ${rolesString}`}
        cancelButtonText="Cancel"
        onConfirm={() => this.handleConfirmSendNotificationsToRoles(roles)}
        subtext={`We’ll send ${notificationType} to the ${rolesString} in your school to claim and use their Remind accounts.`}
      />
    );
  }

  renderNotificationOption(roles: string[], sendButtonText: string, notificationType: string) {
    const rolesString = rolesDescription(roles);

    return (
      <Box key={roles.join('-')}>
        <BoxHeader
          size="small"
          title={`Notify the ${rolesString}?`}
          subtext={`Help your ${rolesString} get started on Remind by sending them ${notificationType} with details of their new accounts.`}
          textAlign="left"
        />
        <BoxBody size="small">
          <Buttons full>
            <Button level="secondary" onClick={() => this.previewMessage(roles)}>
              Learn more
            </Button>
            <ModalTrigger modal={this.renderSendNotificationsModal(roles, notificationType)}>
              <Button>{sendButtonText}</Button>
            </ModalTrigger>
          </Buttons>
        </BoxBody>
      </Box>
    );
  }

  render() {
    const { canNotifyTeachers, canNotifyStudents, canNotifyParents } = this.props;
    const { notifiedGroups } = this.state;

    const options = [];

    if (canNotifyTeachers && !notifiedGroups.includes('teacher')) {
      options.push(this.renderNotificationOption(['teacher'], 'Send email', 'an email'));
    }
    if (
      canNotifyStudents &&
      canNotifyParents &&
      !notifiedGroups.includes('student') &&
      !notifiedGroups.includes('parent')
    ) {
      options.push(
        this.renderNotificationOption(['student', 'parent'], 'Notify them', 'a message')
      );
    } else if (canNotifyStudents && !notifiedGroups.includes('student')) {
      options.push(this.renderNotificationOption(['student'], 'Notify them', 'a message'));
    } else if (canNotifyParents && !notifiedGroups.includes('parent')) {
      options.push(this.renderNotificationOption(['parent'], 'Notify them', 'a message'));
    }

    if (isEmpty(options)) {
      return null;
    }

    return <Columns2>{options}</Columns2>;
  }
}

const withMutations = graphql(SEND_ACTIVATION_NOTIFICATIONS_MUTATION, {
  props({ mutate }) {
    return {
      sendActivationNotifications(input: SendActivationNotificationsInput) {
        return mutate({
          variables: { input },
        });
      },
    };
  },
});

export default withMutations(AdminSisSyncNotificationOptions);
export { AdminSisSyncNotificationOptions };
